/**
 * ApexOutletGroup — collapsible section wrapping a list of outlets.
 *
 * Used by the dashboard once-per-group to render Display Tank,
 * Life Support, Variable Speed, Dosing, Garage, Other, and the
 * (collapsed by default) Virtual Outputs sections.
 *
 * Monitor-only build: every tile in the group is a read-only status
 * display — there are no write affordances anywhere in this tree.
 *
 * The collapsed/expanded state is local: each group can be toggled
 * independently, and the initial state is sourced from the
 * `OUTLET_GROUPS[*].defaultCollapsed` metadata.
 *
 * Group header is implemented as a `<button>` for keyboard a11y; clicking
 * (or pressing Enter/Space) toggles the body. The badge in the header
 * shows the outlet count, plus a small "N on" subtext when at least one
 * outlet in the group is currently powered.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { Badge } from './Badge';
import { ApexOutletTile } from './ApexOutletTile';
import type { ApexOutletSnapshot } from '../lib/types';

export interface ApexOutletGroupProps {
  label: string;
  outlets: readonly ApexOutletSnapshot[];
  defaultCollapsed?: boolean;
  /** Stable id used for `data-testid`s in the rendered DOM. */
  groupId: string;
}

export function ApexOutletGroup({
  label,
  outlets,
  defaultCollapsed = false,
  groupId,
}: ApexOutletGroupProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const onCount = outlets.reduce((n, o) => n + (o.state === 'ON' ? 1 : 0), 0);

  return (
    <section
      data-testid={`apex-outlet-group-${groupId}`}
      data-collapsed={collapsed}
      className="rounded-2xl border border-reef-700/40 bg-reef-900/30"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls={`apex-outlet-group-body-${groupId}`}
        className={clsx(
          'flex w-full items-center justify-between gap-3 px-4 py-2.5',
          'text-left text-sm font-semibold text-reef-100',
          'hover:bg-reef-800/30 transition-colors rounded-2xl',
        )}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className={clsx(
              'inline-block transition-transform text-reef-400',
              collapsed ? 'rotate-0' : 'rotate-90',
            )}
          >
            ▸
          </span>
          {label}
        </span>
        <span className="flex items-center gap-2 text-xs font-normal text-reef-300">
          {onCount > 0 ? <span className="text-emerald-300">{onCount} on</span> : null}
          <Badge variant="neutral">{outlets.length}</Badge>
        </span>
      </button>
      {!collapsed ? (
        <div
          id={`apex-outlet-group-body-${groupId}`}
          className="grid gap-1.5 px-3 pb-3 pt-1"
        >
          {outlets.map((o) => (
            <ApexOutletTile key={o.name} outlet={o} />
          ))}
        </div>
      ) : null}
    </section>
  );
}
